import type React from "react";
import { useRef, useState } from "react";

import _ from "lodash";
import { useDispatch } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { SWReactTableWrapperContextConsumer } from "app/components/React/Table/SWReactTableWrapperSelectionContext";
import { i18nFilter } from "app/filters/i18nFilter";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { keywordGroupsQueryClient, keywordGroupsTransforms } from "app/services/keyword-groups";
import { useCreateOrUpdateKeywordGroup, useKeywordGroups } from "app/services/keyword-groups/hooks";
import { allTrackers } from "app/services/track/track";
import { TableSelection } from "pro-features/components/TableSelection/src/TableSelection";
import { generalGroupToastMessage } from "pro-features/components/TableSelection/src/TableSelectionGroupKeywordComponent";
import { TableSelectionNewGroupDropdown } from "pro-features/components/TableSelection/src/TableSelectionNewGroupDropdown";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { getToastItemComponent } from "../Toast/ToastItem";

const i18n = i18nFilter();
const KEYWORD_ANALYSIS_STATE = "organicsearch_keywordAnalysis_2";
export enum ETableSelectionNewGroupDropdownMode {
    NEW_GROUP,
    ALL_ITEMS_EXISTS,
    MAX_ITEMS,
    GROUP_LIST,
}

export const KeywordSelection: React.FC<{
    selectedRows: any[];
    clearAllSelectedRows: VoidFunction;
    onKeywordsAddedToGroup?: (group: IUserKeywordGroup, flag: boolean) => void;
    notify?: boolean;
    showToast: (a: string, b: string, c: string) => void;
    appendTo: string;
    searchField?: string;
}> = ({ notify = true, searchField = "SearchTerm", ...props }) => {
    const dispatch = useDispatch();
    const dropdownRef = useRef<any>(null);
    const createOrUpdateKeywordGroup = useCreateOrUpdateKeywordGroup();
    const [tableSelectionMode, setTableSelectionMode] = useState<ETableSelectionNewGroupDropdownMode>(
        ETableSelectionNewGroupDropdownMode.GROUP_LIST,
    );
    const [tableSelectionCurrentGroup, setTableSelectionCurrentGroup] = useState<string | undefined>(undefined);
    const [tableSelectionErrorMessage, setTableSelectionErrorMessage] = useState<string | undefined>(undefined);
    const [isTableSelectionLoading, setIsTableSelectionLoading] = useState<boolean>(false);
    const [tableSelectionIsValid, setTableSelectionIsValid] = useState<boolean>(true);
    const maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount();
    const { data: keywordGroups } = useKeywordGroups();

    const tableSelectionOnCancel = () => {
        if (tableSelectionMode === ETableSelectionNewGroupDropdownMode.NEW_GROUP) {
            allTrackers.trackEvent("add to keyword group", "click", "Create New Keyword Group/Cancel");
        }
        setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
        setTableSelectionIsValid(true);
        setTableSelectionErrorMessage(undefined);
    };

    const getSelectedRowsFromStore = () => {
        return props.selectedRows.map((item) => item[searchField]);
    };

    const onNewGroupClick = () => {
        allTrackers.trackEvent("add to keyword group", "click", "create new group");
        if (getSelectedRowsFromStore().length > KeywordGroupEditorHelpers.getMaxGroupCount()) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
        } else {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.NEW_GROUP);
        }
    };

    const showGroupLinkToast = (name: string, text: string, label: string, stateLink?: string) => {
        const { country, duration } = proNavigator.getParams();
        const linkToGroup = proNavigator.href(stateLink ?? "organicsearch_keywordAnalysis_overview_2", {
            country,
            duration,
            keyword: `*${name}`,
        });
        props.showToast?.(linkToGroup, text, label);
    };

    const onExistingGroupClick = async (item: { id: string }) => {
        // @ts-ignore FIXME: strict migration - getGroupById can return null — click flow guarantees an existing group for the given id
        const currentGroup: IUserKeywordGroup = await keywordGroupsQueryClient.getGroupById(item.id);
        const currentKeywords = currentGroup.Keywords.map((keyword) => {
            return { text: keyword };
        });
        const newKeywords = getSelectedRowsFromStore().map((keyword) => {
            return { text: keyword };
        });
        const withoutDuplications = _.uniqWith(
            [...currentKeywords, ...newKeywords],
            (crr, other) => crr.text === other.text,
        );
        const newKeywordsCount = withoutDuplications.length - currentKeywords.length;
        // if no new keywords was added
        setTableSelectionCurrentGroup(currentGroup.Name);
        // await this.setStateAsync({
        //     tableSelectionCurrentGroup:
        // });

        if (newKeywordsCount === 0) {
            setTableSelectionMode(ETableSelectionNewGroupDropdownMode.ALL_ITEMS_EXISTS);
        }
        // new keywords added
        else {
            const group = KeywordGroupEditorHelpers.keywordGroupFromList(
                {
                    title: currentGroup.Name,
                    items: withoutDuplications,
                },
                currentGroup,
            );
            // check number of keywords in group
            if (group.Keywords.length > maxKeywordsInGroup) {
                setTableSelectionMode(ETableSelectionNewGroupDropdownMode.MAX_ITEMS);
            } else {
                const result = await createOrUpdateKeywordGroup.mutateAsync(group);
                if (result) {
                    props.clearAllSelectedRows();
                    setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
                    setTableSelectionIsValid(true);
                    setTableSelectionErrorMessage(undefined);
                    dropdownRef.current?.close();
                    props.onKeywordsAddedToGroup?.(group, false);
                    if (notify) {
                        showGroupLinkToast(
                            currentGroup.Id,
                            generalGroupToastMessage(currentGroup.Name, newKeywordsCount),
                            i18n("table.selection.keywords.seegroup"),
                            KEYWORD_ANALYSIS_STATE,
                        );
                    }
                }
            }
        }
    };

    const onGroupClick = async (item: { id: string } | {}) => {
        // click on new group
        if (!("id" in item && item.id)) {
            onNewGroupClick();
        }
        // click on existing group
        else {
            await onExistingGroupClick(item);
        }
    };

    const tableSelectionOnSubmit = async (name: string) => {
        const { isValid, errorMessage } = await KeywordGroupEditorHelpers.validateTitle(name);
        if (!isValid) {
            setTableSelectionIsValid(isValid);
            setTableSelectionErrorMessage(errorMessage);
        } else {
            // await this.toggleLoading();
            setIsTableSelectionLoading(!isTableSelectionLoading);
            const group = KeywordGroupEditorHelpers.keywordGroupFromList({
                title: name,
                items: getSelectedRowsFromStore().map((keyword) => {
                    return { text: keyword };
                }),
            });
            allTrackers.dispatchTrackEvent(
                "Button",
                "click",
                `Create New Keyword List/Save/${name || ""}/${group.Keywords.length.toString() || ""}`,
            );
            const groupFound = await createOrUpdateKeywordGroup.mutateAsync(group);
            if (groupFound) {
                if (notify) {
                    if (groupFound) {
                        showGroupLinkToast(
                            groupFound.Id,
                            i18n("table.selection.keywords.groupcreated"),
                            i18n("table.selection.keywords.seegroup"),
                        );
                    } else {
                        dispatch(
                            showSuccessToast(
                                getToastItemComponent({
                                    text: i18n("table.selection.keywords.groupcreated"),
                                }),
                            ),
                        );
                    }
                }
                // await this.toggleLoading();
                setIsTableSelectionLoading(!isTableSelectionLoading);
                props.clearAllSelectedRows();
                setTableSelectionMode(ETableSelectionNewGroupDropdownMode.GROUP_LIST);
                setTableSelectionIsValid(true);
                setTableSelectionErrorMessage(undefined);
                dropdownRef.current?.close();
                const addedGroup = groupFound || {};
                props.onKeywordsAddedToGroup?.(
                    {
                        ...group,
                        ...addedGroup,
                    },
                    true,
                );
            } else {
                // error
                dropdownRef.current?.close();
            }
        }
    };

    const getDropdown = () => {
        return (
            <TableSelectionNewGroupDropdown
                appendTo={props.appendTo}
                ref={(ref) => {
                    dropdownRef.current = ref;
                }}
                groups={keywordGroupsTransforms.toDropDown(keywordGroups)}
                onSubmit={tableSelectionOnSubmit}
                isLoading={isTableSelectionLoading}
                error={!tableSelectionIsValid}
                errorMessage={tableSelectionErrorMessage}
                onGroupClick={onGroupClick}
                mode={tableSelectionMode}
                currentGroup={tableSelectionCurrentGroup}
                onCancel={tableSelectionOnCancel}
                groupIconName="keyword-group"
                count={maxKeywordsInGroup}
                newGroupNameLabel={i18n("table.selection.newgroup.keywords.title")}
                trackingCategory="add to keyword group"
                translate={i18n}
                allItemsExistsMessage={i18n("table.selection.keywords.exists")}
                maxItemsMessage={i18n("table.selection.keywords.max.keywords", { count: maxKeywordsInGroup ?? 0 })}
                newGroupItemText={i18n("table.selection.newgroup.keywords.create")}
            />
        );
    };
    return (
        <SWReactTableWrapperContextConsumer>
            {({ selectedRows, clearAllSelectedRows }) => {
                const text = i18n("keyword.generator.tool.table.keywords.selected", {
                    number: selectedRows.length.toString(),
                });
                return (
                    <TableSelection
                        key="1"
                        selectedText={text}
                        onCloseClick={clearAllSelectedRows}
                        addToGroupLabel=""
                        isVisible={selectedRows.length > 0}
                        groupSelectorElement={getDropdown()}
                    />
                );
            }}
        </SWReactTableWrapperContextConsumer>
    );
};
